import { ReactNode, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label as LabelUI } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select as SelectUI,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { cn } from '@/lib/utils';

type SelectOption = {
  id: string | number;
  name: string;
  icon?: ReactNode;
  /**
   * Optional group heading (pass it already translated). Adjacent options with
   * the same group render under one label; options without a group stay flat.
   */
  group?: string;
};

type OptionGroup = {
  label: string | null;
  options: SelectOption[];
};

// A flat list (no groups) yields a single unlabeled group — rendering is unchanged.
const groupOptions = (options: SelectOption[]): OptionGroup[] => {
  const groups: OptionGroup[] = [];
  options.forEach((option) => {
    const label = option.group ?? null;
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.label === label) {
      lastGroup.options.push(option);
    } else {
      groups.push({ label, options: [option] });
    }
  });
  return groups;
};

type SelectProps = {
  value?: string;
  onValueChange?: (value: string) => void;
  options: SelectOption[];
  label?: ReactNode;
  searchPlaceholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  className?: string;
  id?: string;
  'aria-invalid'?: boolean;
  isInvalid?: boolean;
};

const Select = (props: SelectProps) => {
  const {
    value,
    onValueChange,
    options,
    label,
    searchPlaceholder,
    disabled,
    searchable = false,
    className,
    id,
    'aria-invalid': ariaInvalid,
    isInvalid,
  } = props;

  const { t } = useTranslation();

  const generatedId = useId();
  const selectId = id ?? generatedId;

  const [isFocused, setIsFocused] = useState(false);
  const [search, setSearch] = useState('');

  const selectedOption = useMemo(
    () => options.find((opt) => String(opt.id) === value),
    [options, value],
  );

  // Leading nbsp marks hierarchy depth in the dropdown rows — the trigger
  // shows the bare name.
  const selectedName = selectedOption?.name.replace(/^\u00A0+/u, '');

  const renderTriggerValue = (): ReactNode => {
    if (!selectedOption) {
      return '';
    }
    if (!selectedOption.icon) {
      return selectedName;
    }
    return (
      <span className="flex items-center gap-2">
        <span className="flex shrink-0 items-center">
          {selectedOption.icon}
        </span>
        {selectedName}
      </span>
    );
  };

  const isFloating = isFocused || Boolean(selectedOption);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lowerSearch = search.toLowerCase();
    return options.filter((option) =>
      option.name.toLowerCase().includes(lowerSearch),
    );
  }, [options, search]);

  const labelNode = label && (
    <>
      <fieldset
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 top-[-5px] z-1 overflow-hidden rounded-md bg-transparent px-[9px] transition-colors',
          isFocused && !ariaInvalid && !isInvalid && 'border-primary border-2',
          !isFocused && !ariaInvalid && !isInvalid && 'border-input border-2',
          (ariaInvalid || isInvalid) && 'border-destructive border-2',
        )}
      >
        <legend
          className={cn(
            'invisible block h-[11px] w-auto overflow-hidden p-0 text-xs whitespace-nowrap',
            isFloating
              ? 'max-w-full transition-[max-width] delay-50 duration-100 ease-out'
              : 'max-w-[0.01px] transition-[max-width] duration-50 ease-out',
          )}
        >
          <span className="visible inline-block px-1 opacity-0">{label}</span>
        </legend>
      </fieldset>
      <LabelUI
        htmlFor={selectId}
        className={cn(
          'bg-card text-muted-foreground pointer-events-none absolute left-[13px] z-2 gap-1 rounded-xs px-1 transition-all duration-200 ease-out',
          isFloating
            ? 'top-0 -translate-y-1/2 text-xs'
            : 'top-1/2 -translate-y-1/2 text-sm',
          ariaInvalid || isInvalid
            ? 'text-destructive'
            : isFocused && 'text-primary',
        )}
      >
        {label}
      </LabelUI>
    </>
  );

  if (searchable) {
    return (
      <div className="relative">
        <Popover
          modal
          open={isFocused}
          onOpenChange={setIsFocused}
        >
          <PopoverTrigger asChild>
            <Button
              id={selectId}
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={isFocused}
              aria-invalid={ariaInvalid}
              disabled={disabled}
              className={cn(
                'dark:bg-card dark:hover:bg-card h-10! w-full justify-between border-2 border-transparent! bg-transparent font-normal shadow-none ring-0 outline-none hover:bg-transparent focus-visible:border-transparent! focus-visible:ring-0 aria-invalid:border-transparent! dark:border-transparent!',
                className,
              )}
            >
              <span className="flex-1 truncate text-left">
                {selectedName || '\u00A0'}
              </span>
              <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-(--radix-popover-trigger-width) p-0"
            align="start"
            onCloseAutoFocus={() => setSearch('')}
          >
            <div className="border-b p-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={
                  searchPlaceholder ||
                  (typeof label === 'string' ? `${label}...` : 'Search...')
                }
                className="placeholder:text-muted-foreground w-full bg-transparent text-sm outline-none"
              />
            </div>
            <div className="max-h-75 overflow-y-auto p-1">
              {filteredOptions.length === 0 ? (
                <div className="text-muted-foreground py-6 text-center text-sm">
                  {t('No options available')}
                </div>
              ) : (
                groupOptions(filteredOptions).flatMap((group, groupIndex) => [
                  group.label ? (
                    <div
                      key={`label-${group.label}-${groupIndex}`}
                      className="text-muted-foreground px-2 py-1.5 text-xs font-medium"
                    >
                      {group.label}
                    </div>
                  ) : null,
                  ...group.options.map((option) => {
                    const optionIdString = String(option.id);
                    const isSelected = value === optionIdString;
                    return (
                      <div
                        key={optionIdString}
                        role="option"
                        aria-selected={isSelected}
                        className={cn(
                          "[&_svg:not([class*='text-'])]:text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none",
                        )}
                        onClick={() => {
                          onValueChange?.(optionIdString);
                          setIsFocused(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'size-4 shrink-0',
                            isSelected ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        {option.icon && (
                          <span className="flex shrink-0 items-center">
                            {option.icon}
                          </span>
                        )}
                        <span className="flex-1 text-sm">{option.name}</span>
                      </div>
                    );
                  }),
                ])
              )}
            </div>
          </PopoverContent>
        </Popover>
        {labelNode}
      </div>
    );
  }

  return (
    <div className="relative grid grid-cols-[minmax(0,1fr)]">
      <SelectUI
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        onOpenChange={(open) => setIsFocused(open)}
      >
        <SelectTrigger
          id={selectId}
          className={cn(
            'dark:bg-card dark:hover:bg-card h-10! w-full border-2 border-transparent bg-transparent ring-0 outline-none hover:cursor-pointer hover:bg-transparent focus-visible:border-transparent focus-visible:ring-0 aria-invalid:border-transparent aria-invalid:ring-0 *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:overflow-hidden *:data-[slot=select-value]:text-ellipsis',
            className,
          )}
          aria-invalid={ariaInvalid}
        >
          {/* Children must always be defined: with no children Radix portals the
              selected ItemText into this node, and flipping between the two
              modes crashes ("ref ... as a container to createPortal"). */}
          <SelectValue>{renderTriggerValue()}</SelectValue>
        </SelectTrigger>
        <SelectContent
          position="popper"
          sideOffset={4}
          className="w-(--radix-select-trigger-width)"
        >
          {options.length === 0 ? (
            <div className="text-muted-foreground py-6 text-center text-sm">
              {t('No options available')}
            </div>
          ) : (
            groupOptions(options).map((group, groupIndex) => (
              <SelectGroup key={group.label ?? `group-${groupIndex}`}>
                {group.label && <SelectLabel>{group.label}</SelectLabel>}
                {group.options.map((option) => (
                  <SelectItem
                    key={String(option.id)}
                    value={String(option.id)}
                  >
                    {option.icon ? (
                      <span className="flex items-center gap-2">
                        <span className="flex shrink-0 items-center">
                          {option.icon}
                        </span>
                        {option.name}
                      </span>
                    ) : (
                      option.name
                    )}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))
          )}
        </SelectContent>
      </SelectUI>
      {labelNode}
    </div>
  );
};

export { Select };
export type { SelectOption, SelectProps };
